// Modules
import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import {
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';

// Rxjs
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';

// Constants
import { APP_PATHS_CONSTANTS } from '@shared/constants';
import * as AutoConstants from '@car-insurance/shared/constants/index';
import * as FianzaConstants from '@fianza/shared/constants/index';
import * as CarteraConstants from '@cartera/shared/constants/index';
import * as AccidentsConstants from '@accidents/shared/constants/index';
import * as RenewalsConstants from '@renewals-module/utils/constants/renewals.constants';
import * as TransportConstants from '@transport-module/shared/constants/routing';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as TravelConstants from '@travel/utils/constants/index';

// Enums
import { PermissionsEnum } from '@shared/enums/permissions';

// Reducer
import { AppState } from '@store/reducers';

// Selectors
import * as AuthSelectors from '@store/selectors/auth.selectors';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private _helper = new JwtHelperService();
  private _userPermissions: string[] = [];

  // Constructor
  constructor(
    private _store: Store<AppState>,
    private _router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this._store.pipe(
      select(AuthSelectors.selectLoggedUser),
      map(user => {
        if (!user) {
          this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_LOGIN);
          return false;
        } else {
          const decodedToken = this._helper.decodeToken(user.accessToken);
          this._userPermissions = decodedToken
            ? decodedToken.Permissions
              ? decodedToken.Permissions.split(',')
              : []
            : [];
          return this._checkPermissions(
            this._userPermissions,
            route.url,
            state.url
          );
        }
      }),
      take(1)
    );
  }

  private _checkPermissions(permissionsArray, routeUrl, stateUrl): boolean {
    // Users
    if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.USERS_MODULE}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Usuarios_Administrar_Usuarios
        ) ||
        permissionsArray.includes(
          PermissionsEnum.Administrador_Usuarios_Administrar_Roles_Permisos
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(
          `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.DASHBOARD_AUTO}`
        );
        return false;
      }
    } else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.FIANZA_MODULE}/${FianzaConstants.MODULE_PATHS_CONSTANTS.NEW_CUPO}`
      )
    ) {
      if (permissionsArray.includes(PermissionsEnum.Fianzas_Aprobar_Cupo)) {
        return true;
      } else {
        this._router.navigateByUrl(
          `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.DASHBOARD_AUTO}`
        );
        return false;
      }
    }
    // Cartera
    else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.CARTERA_MODULE}/${CarteraConstants.MODULE_PATHS_CONSTANTS.LISTADO_CARTERA}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Cartera_Seguimiento_Polizas
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
        return false;
      }
    } else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.CARTERA_MODULE}/${CarteraConstants.MODULE_PATHS_CONSTANTS.LISTADO_DEUDORES}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Cartera_Seguimiento_Saldos_Vencimientos
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
        return false;
      }
    }
    // New Póliza
    else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.FIANZA_MODULE}/${FianzaConstants.MODULE_PATHS_CONSTANTS.NEW_POLIZA}`
      )
    ) {
      if (permissionsArray.includes(PermissionsEnum.Fianzas_Aprobar_Poliza)) {
        return true;
      } else {
        this._router.navigateByUrl(
          `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.DASHBOARD_AUTO}`
        );
        return false;
      }
      // Siniestros Autos
    } else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.ACCIDENTS_MODULE}/${AccidentsConstants.MODULE_PATHS_CONSTANTS.CARS}`
      )
    ) {
      if (permissionsArray.includes(PermissionsEnum.Siniestros_Ingreso)) {
        return true;
      } else {
        this._router.navigateByUrl(
          `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.DASHBOARD_AUTO}`
        );
        return false;
      }
      // Siniestros transporte
    } else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.ACCIDENTS_MODULE}/${AccidentsConstants.MODULE_PATHS_CONSTANTS.SHIPS}`
      )
    ) {
      if (permissionsArray.includes(PermissionsEnum.Siniestros_Consulta)) {
        return true;
      } else {
        this._router.navigateByUrl(
          `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.DASHBOARD_AUTO}`
        );
        return false;
      }
      // Campañas
    } else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.COUPONS_CAMPAIGN}`
      )
    ) {
      if (permissionsArray.includes(PermissionsEnum.Crear_Campanias)) {
        return true;
      } else {
        this._router.navigateByUrl(
          `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.DASHBOARD_AUTO}`
        );
        return false;
      }
      // Cuentas
    } else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.ACCOUNTS}`
      )
    ) {
      if (permissionsArray.includes(PermissionsEnum.Gestion_Cuentas)) {
        return true;
      } else {
        this._router.navigateByUrl(
          `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.DASHBOARD_AUTO}`
        );
        return false;
      }
      // Cartera
    } else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.CARTERA_MODULE}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Cartera_Seguimiento_Saldos_Vencimientos
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(
          `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.DASHBOARD_AUTO}`
        );
        return false;
      }
      // Auto
    } else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.CAR_INSURANCE_MODULE}/${AutoConstants.MODULE_PATHS_CONSTANTS.CAR_FILE_UPLOAD}`
      ) ||
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.CAR_INSURANCE_MODULE}/${AutoConstants.MODULE_PATHS_CONSTANTS.SETTINGS_BDEO}`
      ) ||
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.CAR_INSURANCE_MODULE}/${AutoConstants.MODULE_PATHS_CONSTANTS.CAR_INSURANCE_COEFFICIENTS}`
      ) ||
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.CAR_INSURANCE_MODULE}/${AutoConstants.MODULE_PATHS_CONSTANTS.CAR_INSURANCE_PARAMETERS}`
      ) ||
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.CAR_INSURANCE_MODULE}/${AutoConstants.MODULE_PATHS_CONSTANTS.CAR_NEW_MODEL}`
      )
    ) {
      if (
        permissionsArray.includes(PermissionsEnum.Sistema_Edicion_Parametros)
      ) {
        return true;
      } else {
        this._router.navigateByUrl(
          `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.DASHBOARD_AUTO}`
        );
        return false;
      }
    } else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.CAR_INSURANCE_MODULE}/${AutoConstants.MODULE_PATHS_CONSTANTS.CAR_NEW_COVERAGE}`
      ) ||
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.CAR_INSURANCE_MODULE}/${AutoConstants.MODULE_PATHS_CONSTANTS.CAR_NEW_PACKAGE}`
      )
    ) {
      if (
        permissionsArray.includes(PermissionsEnum.Sistema_Coberturas_Paquetes)
      ) {
        return true;
      } else {
        this._router.navigateByUrl(
          `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.DASHBOARD_AUTO}`
        );
        return false;
      }
    } else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.CAR_INSURANCE_MODULE}/${AutoConstants.MODULE_PATHS_CONSTANTS.QUOTES_AUDIT}`
      ) ||
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.CAR_INSURANCE_MODULE}/${AutoConstants.MODULE_PATHS_CONSTANTS.FAILED_QUOTES}`
      )
    ) {
      if (permissionsArray.includes(PermissionsEnum.Sistema_Cotizaciones)) {
        return true;
      } else {
        this._router.navigateByUrl(
          `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.DASHBOARD_AUTO}`
        );
        return false;
      }
    }
    // Renovaciones automóviles
    else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.RENEWALS}/${RenewalsConstants.MODULE_PATHS_CONSTANTS.LISTADO_AUTOMOVILES}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Renovaciones_Automoviles
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
        return false;
      }
    }
    // Renovaciones multiseguros
    else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.RENEWALS}/${RenewalsConstants.MODULE_PATHS_CONSTANTS.LISTADO_MULTISEGUROS}`
      )
    ) {
      if (
        permissionsArray.includes(PermissionsEnum.Administrador_Multiproducto)
      ) {
        return true;
      } else {
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
        return false;
      }
    }
    // Renovaciones garantía de alquiler
    else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.RENEWALS}/${RenewalsConstants.MODULE_PATHS_CONSTANTS.LISTADO_GARANTIAS}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Renovaciones_Garantia_Alquiler
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
        return false;
      }
    }
    // Pólizas Transporte
    else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.TRANSPORT}/${TransportConstants.MODULE_PATHS_CONSTANTS.LISTADO_POLIZAS}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Transporte_ReadPolicy
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
        return false;
      }
    }
    // Embarques Transporte
    else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.TRANSPORT}/${TransportConstants.MODULE_PATHS_CONSTANTS.LISTADO_EMBARQUES}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Transporte_ReadShippment
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
        return false;
      }
    }
    // Facturación Transporte
    else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.TRANSPORT}/${TransportConstants.MODULE_PATHS_CONSTANTS.LISTADO_FACTURACION}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Transporte_BillShippment
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
        return false;
      }
    }
    // Facturación fallida Transporte
    else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.TRANSPORT}/${TransportConstants.MODULE_PATHS_CONSTANTS.FACTURACION_FALLIDA}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Transporte_BillShippment
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
        return false;
      }
    }
    // Emisión fallida Transporte
    else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.TRANSPORT}/${TransportConstants.MODULE_PATHS_CONSTANTS.EMISION_FALLIDA}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Transporte_ReadPolicy
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
        return false;
      }
    } else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.TRANSPORT}/${TransportConstants.MODULE_PATHS_CONSTANTS.CATEGORIAS}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Transporte_Configuracion_Categorias
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
        return false;
      }
      // Listado de pólizas de Viajes
    } else if (
      stateUrl.includes(
        `/${APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER}/${APP_PATHS_CONSTANTS.TRAVEL}/${TravelConstants.MODULE_PATHS_CONSTANTS.POLICIES_LIST}`
      )
    ) {
      if (
        permissionsArray.includes(
          PermissionsEnum.Administrador_Viajes_Seguimiento_Polizas_Cotizaciones
        )
      ) {
        return true;
      } else {
        this._router.navigateByUrl(APP_PATHS_CONSTANTS.ADMIN_PRIVATE_WRAPPER);
        return false;
      }
    } else {
      return true;
    }
  }
}
